import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ShieldCheck, Sparkles, Store } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EnquiryDialog } from "@/components/discovery/EnquiryDialog";

export const FeaturedRail = ({ categorySlug, categoryName, popularSubs = [] }) => {
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    api.get(`/discovery/featured${categorySlug ? `?category=${categorySlug}` : ""}`).then((r) => setVendors(r.data)).catch(() => {});
  }, [categorySlug]);

  return (
    <div className="space-y-4" data-testid="featured-rail">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-500" /> Featured & Sponsored</p>
        <div className="space-y-2">
          {vendors.map((v) => (
            <Link key={v.slug} to={`/vendors/vendor/${v.slug}`} className="block rounded-xl border border-slate-100 hover:border-teal-brand/30 p-2.5" data-testid={`rail-vendor-${v.slug}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-slate-800 line-clamp-1">{v.name}</span>
                {v.sponsored ? <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[9px] font-bold uppercase shrink-0">Sponsored</span>
                  : <span className="rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5 text-[9px] font-bold uppercase shrink-0">Featured</span>}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">{v.city} · Demo profile</p>
            </Link>
          ))}
          {!vendors.length && <p className="text-xs text-slate-400">No featured vendors yet.</p>}
        </div>
        <p className="mt-2 text-[10px] text-slate-400">Paid placement does not affect complaint moderation or dispute outcomes.</p>
      </div>

      <div className="rounded-2xl border border-teal-brand/20 bg-teal-brand/5 p-4">
        <p className="text-sm font-bold text-slate-900 flex items-center gap-1"><Sparkles className="h-4 w-4 text-teal" /> Quick Enquiry</p>
        <p className="text-xs text-slate-500 mt-1 mb-3">Tell us what you need and we'll route it to relevant providers.</p>
        <EnquiryDialog categorySlug={categorySlug} categoryName={categoryName}
          trigger={<Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" data-testid="rail-enquiry-btn">Submit an Enquiry</Button>} />
      </div>

      {popularSubs.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Popular Subcategories</p>
          <div className="flex flex-wrap gap-1.5">
            {popularSubs.slice(0, 8).map((s) => (
              <Link key={s.slug} to={`/vendors/${categorySlug}/${s.slug}`} className="rounded-full bg-slate-100 hover:bg-teal-brand/10 hover:text-teal text-slate-600 px-2.5 py-1 text-[11px]">{s.name}</Link>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><Store className="h-3.5 w-3.5 text-teal" /> Are you a business?</p>
        <p className="text-[12px] text-slate-500 mb-2">List your business in our directory and receive relevant enquiries.</p>
        <Link to="/vendors/list-your-business"><Button variant="outline" className="w-full" data-testid="rail-list-business">List your business</Button></Link>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-teal" /> Safety & Verification</p>
        <ul className="text-[12px] text-slate-500 space-y-1.5 list-disc pl-4">
          <li>Always request a written estimate before work begins.</li>
          <li>Contact details are shared only with your consent.</li>
          <li>Demo content is clearly labelled and is not a real review.</li>
          <li>ComplaintAdvisor does not guarantee outcomes.</li>
        </ul>
      </div>
    </div>
  );
};
